import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { OpenPositionsFunnel } from '../types/openPositions';
import type { PipelineTrends } from '../types/pipelineTrends';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export async function fetchOpenPositionsFunnel(): Promise<OpenPositionsFunnel> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/recruiting/open-positions-funnel`);
  if (!res.ok) {
    throw new Error(`Open positions funnel fetch failed: ${res.status}`);
  }
  return res.json();
}

export interface HygieneNudgeResult {
  sent: boolean;
  stale: number;
  reason?: string;
}

/**
 * Ask the TA channel to tidy the stale offer records.
 *
 * ⚠️ It sends NO figures. The proxy re-measures them, because a stale tab posting "47 stale offers"
 * into a team channel is exactly the failure a nudge like this must not have — the whole value of
 * the message is that its number can be trusted. The page URL travels so the reader can get back
 * to where it was counted.
 */
export async function sendOfferHygieneNudge(pageUrl: string): Promise<HygieneNudgeResult> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/recruiting/offer-hygiene-nudge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageUrl }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `The nudge could not be sent: ${res.status}`);
  }
  return res.json();
}

/**
 * The weekly pipeline series behind Tracking's Prospects and Trends tiles.
 *
 * Its own call, not folded into the funnel's: the funnel answers "where are the candidates right
 * now" and this is a series the nightly ETL moves once a day, so they cache differently — and one
 * failing must not take the other down.
 */
export async function fetchPipelineTrends(): Promise<PipelineTrends> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/recruiting/pipeline-trends`);
  if (!res.ok) {
    throw new Error(`Pipeline trends fetch failed: ${res.status}`);
  }
  return res.json();
}
